<template>
    <div class="modal">
        <div class="modal-content">
            <h3>Meeting Check-In</h3>
            <div class="attendance-toggle">
                <label>
                    <input 
                        type="checkbox" 
                        v-model="checkIn.attending"
                    >
                    I will attend this meeting
                </label>
            </div>

            <!-- Show substitute section only if not attending -->
            <div v-if="!checkIn.attending" class="substitute-section">
                <label>
                    <input 
                        type="checkbox" 
                        v-model="checkIn.has_substitute"
                    >
                    I am sending a substitute
                </label>
                <div v-if="checkIn.has_substitute" class="substitute-search">
                    <!-- Show selected substitute -->
                    <div v-if="selectedSubstitute" class="selected-visitor">
                        <span>{{ selectedSubstitute.first_name }} {{ selectedSubstitute.last_name }}</span>
                        <button @click="clearSubstitute" class="remove-visitor">×</button>
                    </div>
                    <!-- Show search only if no substitute selected -->
                    <div v-else>
                        <VisitorSearchCombo
                            placeholder="Search for substitute..."
                            @visitor-selected="handleSubstituteSelected"
                            :disabled-visitors="[...selectedVisitors, selectedSubstitute].filter(Boolean)"
                        />
                    </div>
                </div>
            </div>

            <!-- Visitors Section - always visible -->
            <div class="visitors-section">
                <label>
                    <input 
                        type="checkbox" 
                        v-model="checkIn.has_visitors"
                    >
                    I am bringing visitors
                </label>
                <div v-if="checkIn.has_visitors" class="visitors-list">
                    <div v-for="(visitor, index) in selectedVisitors" 
                         :key="visitor.id" 
                         class="selected-visitor">
                        <span>{{ visitor.first_name }} {{ visitor.last_name }}</span>
                        <button @click="removeVisitor(index)" class="remove-visitor">×</button>
                    </div>
                    
                    <VisitorSearchCombo
                        placeholder="Search for visitors..."
                        @visitor-selected="addVisitor"
                        :disabled-visitors="[...selectedVisitors, selectedSubstitute].filter(Boolean)"
                    />
                    <p class="email-notice">Invitations will automatically be emailed to your visitors.</p>
                </div>
            </div>

            <div class="check-in-form">
                <div v-if="!allScoresSet" class="scores-warning">
                    Please set all scores before checking in
                </div>
                <!-- Business Score -->
                <div class="score-section">
                    <label>How is your business going?</label>
                    <div class="score-slider">
                        <input 
                            type="range" 
                            v-model="checkIn.business_score" 
                            min="0" 
                            max="10" 
                            step="1"
                        >
                        <span class="score-value">{{ checkIn.business_score }}/10</span>
                    </div>
                </div>

                <!-- Personal Score -->
                <div class="score-section">
                    <label>How is your personal life?</label>
                    <div class="score-slider">
                        <input 
                            type="range" 
                            v-model="checkIn.personal_score" 
                            min="0" 
                            max="10" 
                            step="1"
                        >
                        <span class="score-value">{{ checkIn.personal_score }}/10</span>
                    </div>
                </div>

                <!-- Health Score -->
                <div class="score-section">
                    <label>How is your health?</label>
                    <div class="score-slider">
                        <input 
                            type="range" 
                            v-model="checkIn.health_score" 
                            min="0" 
                            max="10" 
                            step="1"
                        >
                        <span class="score-value">{{ checkIn.health_score }}/10</span>
                    </div>
                </div>

                <!-- Comments -->
                <div class="comments-section">
                    <label>Additional Comments (Optional)</label>
                    <textarea 
                        v-model="checkIn.comments"
                        rows="3"
                        placeholder="Any comments you'd like to share..."
                    ></textarea>
                </div>
            </div>

            <div class="modal-actions">
                <button @click="$emit('close')" class="cancel-btn">Cancel</button>
                <button 
                    @click="submitCheckIn" 
                    class="submit-btn" 
                    :disabled="isSubmitting || !allScoresSet"
                    :class="{ 'disabled': !allScoresSet }"
                >
                    {{ isSubmitting ? 'Submitting...' : submitButtonText }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue';
import { checkinService } from '../services/checkinService';
import { eventVisitorService } from '../services/eventVisitorService';
import VisitorSearchCombo from './VisitorSearchCombo.vue';

export default {
    name: 'CheckInModal',
    components: {
        VisitorSearchCombo
    },
    emits: ['close', 'check-in'],
    props: {
        event: {
            type: Object,
            required: true
        },
        existingCheckIn: {
            type: Object,
            default: null
        }
    },
    setup(props, { emit }) {
        const checkIn = ref({
            event_id: props.event?.id,
            business_score: props.existingCheckIn?.business_score ?? 0,
            personal_score: props.existingCheckIn?.personal_score ?? 0,
            health_score: props.existingCheckIn?.health_score ?? 0,
            comments: props.existingCheckIn?.comments ?? '',
            attending: props.existingCheckIn?.attending ?? true,
            has_visitors: props.existingCheckIn?.has_visitors ?? false,
            visitor_names: props.existingCheckIn?.visitor_names ?? '',
            has_substitute: props.existingCheckIn?.has_substitute ?? false,
            substitute_name: props.existingCheckIn?.substitute_name ?? ''
        });

        const isSubmitting = ref(false);

        const submitCheckIn = async () => {
            if (!props.event?.id) {
                console.error('Cannot submit check-in: Invalid event');
                return;
            }

            if (isSubmitting.value) return;
            isSubmitting.value = true;
            try {
                const checkInResponse = await checkinService.create({
                    event_id: checkIn.value.event_id,
                    attending: checkIn.value.attending,
                    business_score: checkIn.value.business_score,
                    personal_score: checkIn.value.personal_score,
                    health_score: checkIn.value.health_score,
                    comments: checkIn.value.comments,
                    has_visitors: checkIn.value.has_visitors,
                    visitor_names: checkIn.value.visitor_names,
                    has_substitute: checkIn.value.has_substitute,
                    substitute_name: checkIn.value.substitute_name
                });

                const memberId = checkInResponse.member_id;

                // Get existing event visitors for comparison
                let existingVisitors = [];
                let existingSubstitutes = [];
                try {
                    const allExistingVisitors = await eventVisitorService.getEventVisitors(props.event.id);
                    existingVisitors = allExistingVisitors.filter(v => v.attendee_type === 'visitor' && v.member_id === memberId);
                    existingSubstitutes = allExistingVisitors.filter(v => v.attendee_type === 'substitute' && v.member_id === memberId);
                } catch (error) {
                    console.error('Error fetching existing visitors:', error);
                }

                // Handle substitute changes
                if (checkIn.value.has_substitute && selectedSubstitute.value) {
                    const existingSubstitute = existingSubstitutes.find(s => 
                        s.visitor_id === (selectedSubstitute.value.visitor_id || selectedSubstitute.value.id)
                    );
                    
                    if (!existingSubstitute) {
                        // New substitute - add with email disabled (substitutes don't get emails)
                        try {
                            await eventVisitorService.addVisitorToEvent({
                                event_id: props.event.id,
                                visitor_id: selectedSubstitute.value.visitor_id || selectedSubstitute.value.id,
                                member_id: memberId,
                                attendee_type: 'substitute'
                            }, false); // No email for substitutes
                        } catch (error) {
                            console.error('Error saving substitute:', error);
                        }
                    }
                    
                    // Remove any existing substitutes that are no longer selected
                    for (const existingSub of existingSubstitutes) {
                        if (existingSub.visitor_id !== (selectedSubstitute.value.visitor_id || selectedSubstitute.value.id)) {
                            try {
                                await eventVisitorService.removeVisitorFromEvent(existingSub.id);
                            } catch (error) {
                                console.error('Error removing old substitute:', error);
                            }
                        }
                    }
                } else {
                    // No substitute selected - remove any existing substitutes
                    for (const existingSub of existingSubstitutes) {
                        try {
                            await eventVisitorService.removeVisitorFromEvent(existingSub.id);
                        } catch (error) {
                            console.error('Error removing substitute:', error);
                        }
                    }
                }

                // Handle visitor changes
                if (checkIn.value.has_visitors && selectedVisitors.value.length > 0) {
                    const selectedVisitorIds = selectedVisitors.value.map(v => v.visitor_id || v.id);
                    
                    // Add new visitors (only email the new ones)
                    for (const visitor of selectedVisitors.value) {
                        const visitorId = visitor.visitor_id || visitor.id;
                        const existingVisitor = existingVisitors.find(v => v.visitor_id === visitorId);
                        
                        if (!existingVisitor) {
                            // This is a new visitor - add with email enabled
                            try {
                                await eventVisitorService.addVisitorToEvent({
                                    event_id: props.event.id,
                                    visitor_id: visitorId,
                                    member_id: memberId,
                                    attendee_type: 'visitor'
                                }, true); // Send email for new visitors
                            } catch (error) {
                                console.error('Error saving new visitor:', error);
                            }
                        }
                    }
                    
                    // Remove visitors that are no longer selected
                    for (const existingVisitor of existingVisitors) {
                        if (!selectedVisitorIds.includes(existingVisitor.visitor_id)) {
                            try {
                                await eventVisitorService.removeVisitorFromEvent(existingVisitor.id);
                            } catch (error) {
                                console.error('Error removing visitor:', error);
                            }
                        }
                    }
                } else {
                    // No visitors selected - remove any existing visitors
                    for (const existingVisitor of existingVisitors) {
                        try {
                            await eventVisitorService.removeVisitorFromEvent(existingVisitor.id);
                        } catch (error) {
                            console.error('Error removing visitor:', error);
                        }
                    }
                }

                emit('check-in');
                emit('close');
            } catch (error) {
                console.error('Error submitting check-in:', error);
            } finally {
                isSubmitting.value = false;
            }
        };

        const closeModal = () => {
            emit('close');
        };

        const updateSliderBackground = (e) => {
            const target = e.target;
            const value = (target.value - target.min) / (target.max - target.min) * 100;
            target.style.setProperty('--value', `${value}%`);
        };

        const selectedSubstitute = ref(null);
        const selectedVisitors = ref([]);
        
        const addVisitor = (visitor) => {
            // Don't add if already selected or is current substitute
            if (selectedVisitors.value.some(v => v.id === visitor.id) ||
                (checkIn.value.has_substitute && selectedSubstitute.value?.id === visitor.id)) {
                return;
            }
            selectedVisitors.value.push(visitor);
        };
        
        const removeVisitor = (index) => {
            selectedVisitors.value.splice(index, 1);
        };

        const handleSubstituteSelected = (visitor) => {
            checkIn.value.substitute_name = `${visitor.first_name} ${visitor.last_name}`;
            selectedSubstitute.value = visitor;
        };

        // Load existing event visitors when editing
        const loadExistingVisitors = async () => {
            if (!props.event?.id || !props.existingCheckIn) return;
            
            try {
                const eventVisitors = await eventVisitorService.getEventVisitors(props.event.id);
                
                // Set substitute
                const substitute = eventVisitors.find(v => v.attendee_type === 'substitute');
                
                if (substitute) {
                    selectedSubstitute.value = substitute;
                    checkIn.value.has_substitute = true;
                    checkIn.value.substitute_name = `${substitute.first_name} ${substitute.last_name}`;
                }
                
                // Set visitors
                const visitors = eventVisitors.filter(v => v.attendee_type === 'visitor');
                selectedVisitors.value = visitors;
                if (visitors.length > 0) {
                    checkIn.value.has_visitors = true;
                }
            } catch (error) {
                console.error('Error loading event visitors:', error);
            }
        };

        const clearSubstitute = () => {
            selectedSubstitute.value = null;
            checkIn.value.substitute_name = '';
        };

        // Watch for changes in attendance status
        watch(() => checkIn.value.attending, (isAttending) => {
            if (isAttending) {
                // Clear substitute when switching to attending
                checkIn.value.has_substitute = false;
                checkIn.value.substitute_name = '';
                selectedSubstitute.value = null;
            }
        });

        onMounted(() => {
            loadExistingVisitors();
            const sliders = document.querySelectorAll('input[type="range"]');
            sliders.forEach(slider => {
                updateSliderBackground({ target: slider });
                slider.addEventListener('input', updateSliderBackground);
            });
        });

        // Computed property to check if all scores have been set
        const allScoresSet = computed(() => {
            return checkIn.value.business_score > 0 && 
                   checkIn.value.health_score > 0 && 
                   checkIn.value.personal_score > 0;
        });

        // Update button text and disable state
        const submitButtonText = computed(() => {
            return props.existingCheckIn ? 'Update Check-in' : 'Check In';
        });

        const isSubmitDisabled = computed(() => {
            return !allScoresSet.value || 
                   (checkIn.value.attending === false && 
                    checkIn.value.has_substitute === false);
        });

        return {
            checkIn,
            isSubmitting,
            submitCheckIn,
            closeModal,
            handleSubstituteSelected,
            selectedVisitors,
            addVisitor,
            removeVisitor,
            selectedSubstitute,
            clearSubstitute,
            allScoresSet,
            submitButtonText,
            isSubmitDisabled
        };
    }
};
</script>

<style scoped>
.modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 2rem;
    z-index: 1000;
}

.modal-content {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    width: 100%;
    max-width: 500px;
}

.score-section {
    margin-bottom: 1.5rem;
}

.score-section label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
}

.score-slider {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.score-slider input[type="range"] {
    -webkit-appearance: none;
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background: #ddd;
    outline: none;
    padding: 0;
    margin: 0;
}

.score-slider input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #4285f4;
    cursor: pointer;
    margin-top: -6px;
}

.score-slider input[type="range"]::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border: 0;
    border-radius: 50%;
    background: #4285f4;
    cursor: pointer;
}

.score-slider input[type="range"]::-webkit-slider-runnable-track {
    width: 100%;
    height: 4px;
    cursor: pointer;
    background: linear-gradient(to right, #4285f4 var(--value), #ddd var(--value));
    border-radius: 2px;
    border: 0;
}

.score-slider input[type="range"]::-moz-range-track {
    width: 100%;
    height: 4px;
    cursor: pointer;
    background: #ddd;
    border-radius: 2px;
    border: 0;
}

.score-value {
    min-width: 3rem;
    text-align: right;
}

.comments-section {
    margin-top: 2rem;
}

.comments-section textarea {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    resize: vertical;
}

.modal-actions {
    margin-top: 2rem;
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
}

.cancel-btn, .submit-btn {
    padding: 0.5rem 1.5rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.cancel-btn {
    background: #6c757d;
    color: white;
}

.submit-btn {
    background: #4285f4;
    color: white;
}

.submit-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.attendance-toggle {
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 4px;
}

.attendance-toggle label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
}

.visitors-section,
.substitute-section {
    margin: 1rem 0;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 8px;
}

.visitor-names,
.substitute-name {
    width: 100%;
    margin-top: 0.5rem;
    padding: 0.5rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
}

.visitor-names {
    resize: vertical;
}

label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
}

input[type="checkbox"] {
    appearance: none;
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    border: 2px solid #4285f4;
    border-radius: 3px;
    margin-right: 8px;
    position: relative;
    cursor: pointer;
    vertical-align: middle;
}

input[type="checkbox"]:checked {
    background-color: #4285f4;
}

input[type="checkbox"]:checked:after {
    content: '';
    position: absolute;
    left: 5px;
    top: 2px;
    width: 4px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}

.cancel-btn, .submit-btn {
    font-weight: 500;
    transition: background-color 0.2s;
}

.cancel-btn:hover {
    background: #5a6268;
}

.submit-btn:hover:not(:disabled) {
    background: #357abd;
}

.substitute-search {
    margin-top: 0.5rem;
}

.visitors-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.selected-visitor {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem;
    background: #f5f5f5;
    border-radius: 4px;
}

.remove-visitor {
    background: none;
    border: none;
    color: #dc3545;
    cursor: pointer;
    font-size: 1.2rem;
    padding: 0 0.5rem;
}

.remove-visitor:hover {
    color: #c82333;
}

.scores-warning {
    color: #dc3545;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    text-align: center;
}

.submit-btn.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #6c757d;
}

.email-notice {
    font-size: 0.75rem;
    color: #666;
    margin-top: 0.5rem;
    margin-bottom: 0;
    font-style: italic;
}
</style> 